
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate }     from 'react-router-dom';
import {
    FormControl,
    Grid,
    MenuItem,
    Select,
} from '@mui/material';
import styled from 'styled-components';


import { EAppRoutes, EAuthRoutes }                         from '../../shared/constants/constants';
import { LogoWhite }                                       from '../../shared/components/Logo';
import { useFetchFamilies, useFetchFamilyUser }                                from '../../shared/queries/family';
import { AuthSVGSceneHolder, PageHeader, SignUpContainer } from '../shared/styles';
import { capitalizeFirstLetter }                           from '../../shared/utils/commonFunctions';
import { useFetchSelectedFamily, useUpdateSelectedFamily } from '../../shared/components/SelectFamily/queries';
import { MuiButton }                                       from '../../shared/components/MuiButton';
import { logout }                                          from '../../shared/utils/setAuthorizationToken';

import imageCheckout  from '../../../images/chose-your-plan-select-page.png';
import { isAndroid, isNative } from '../../shared/utils/platform';
import { ActionPerformed, PushNotifications, Token } from '@capacitor/push-notifications';
import { useFetchChatsList } from '../../modules/MessageCenter/queries';
import { syncBadgeCount } from '../../shared/utils/badge';
import axios from 'axios';
import { queryParams } from '../../shared/functions';
import qs from 'qs';

interface ISelectFamilyProps {
  fullWidth?: boolean;
}

const StyledDropdown = styled(FormControl)<ISelectFamilyProps>`
  flex         : 1;
  width        : 100%;
  margin-bottom: 30px !important;

  .MuiSelect-outlined {
    border-radius   : 9px;
    background-color: #fafafa;
    box-shadow      : 0 0 8px 0 rgb(0 0 0 / 12%);
  }
  
  .MuiInputBase-root {
    margin-top: 0;
    line-height: 1.1876em;
     padding: 0;
    max-width : ${ ({ fullWidth }) => fullWidth ? '100%' : '200px' };
    
    ${ ({ theme: { breakpoints } }) => breakpoints.up('sm') } {
      max-width: ${ ({ fullWidth }) => fullWidth ? '100%' : '260px' };
    }
  }
`;

const FamilyName = styled.div`
  overflow     : hidden;
  text-overflow: ellipsis;
`;

const StyledLogo = styled(LogoWhite)`
  height       : 50px;
  width        : 110px;
  margin-bottom: 40px;
  
  ${ ({ theme: { breakpoints } }) => breakpoints.up('md') } {
    height       : 60px;
    width        : 130px;
    margin-bottom: 90px;
  }
`;

const WrapperGrid = styled(Grid)`
  justify-content: center;
`;

const CreateFamilyWrapperGrid = styled(Grid)`
    display: flex;
    button{
        font-size: 0.6em !important;
    }
`;

const SelectPlanWrapper = styled.div`
  background-color: #5274be;
  height          : 100%;
`;

const TittleText = styled.p`
  margin-bottom  : 40px;
  display        : flex;
  justify-content: center;
`;

const TittleTextExpired = styled.p`
  margin-bottom  : 40px;
  display        : flex;
  justify-content: center;
  font-size      : 18px;
  text-align     : justify;
`;

const SelectPlanContainer = styled.div`&& {
  width     : 200px;
  text-align: center;
  
  ${ ({ theme: { breakpoints } }) => breakpoints.up('sm') } {
    margin: 0;
  }
  
  ${ ({ theme: { breakpoints } }) => breakpoints.up('md') } {
    width: auto;
    margin: 0;
    text-align: left;
  }
}`;

const HeaderContainer = styled(SignUpContainer)`&& {
  padding   : 28px;
  min-height: auto;
  
  ${ ({ theme: { breakpoints } }) => breakpoints.up('sm') } {
    padding: 28px 40px 60px;
  }
  
  ${ ({ theme: { breakpoints } }) => breakpoints.up('md') } {
    padding: 28px 0 40px;
  }
}`;

export const SelectFamilyComponent = () => {
  const history      = useNavigate();
  const { pathname } = useLocation();

  const { data: families }               = useFetchFamilies();
  const { data: selected, isLoading }    = useFetchSelectedFamily();
  const { data: user } = useFetchFamilyUser(selected?.id, { enabled: !!selected });

  const { mutate: updateSelectedFamily, isLoading: isLoadingSelect } = useUpdateSelectedFamily(() => {
    history({ pathname });
  });

  const { data: chatRooms, isFetching: fetchingRooms, dataUpdatedAt: chatRoomsUpdatedAt } = useFetchChatsList(user?.id);
  const mountedAtRef = useRef(Date.now());

  const totalUnreadMessages = useMemo(() =>
    chatRooms?.reduce((acc, curr) => acc + curr.unreadMessagesCount, 0) ?? 0,
  [chatRooms]);

  useEffect(() => {
    if (
      isNative &&
      chatRooms !== undefined &&
      !fetchingRooms &&
      chatRoomsUpdatedAt > mountedAtRef.current
    ) {
      syncBadgeCount(totalUnreadMessages);
    }
  }, [chatRooms, totalUnreadMessages, fetchingRooms, chatRoomsUpdatedAt]);

  const [ isUser, setIsUser ]                     = useState(false);
  const [ page, setPage ]                         = useState('');
  const [ selectedFamilyId, setSelectedFamilyId ] = useState('');
  const [ cookieConsent, setCookieConsent ]       = useState<string | null>(null);
  const [ isCheckingNotification, setIsCheckingNotification ] = useState(isAndroid && isNative);

  useEffect(
    () => {
      if (!isNative) return;

      // On Android cold start, briefly suppress SelectFamily UI while we wait
      // for pushNotificationActionPerformed to fire (native bridge has ~100-300ms latency).
      // If no notification tap triggered this launch, the timeout reveals the screen normally.
      let notificationCheckTimeout: ReturnType<typeof setTimeout> | null = null;
      if (isAndroid) {
        notificationCheckTimeout = setTimeout(() => {
          setIsCheckingNotification(false);
        }, 500);
      }

      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === 'granted') {
          PushNotifications.register();
        } else {
          throw new Error('User denied permissions!');
        }
      });

      PushNotifications.addListener('registration', (token: Token) => {
        const storedToken = localStorage.getItem('fcmRegistrationToken');
        if (token.value !== storedToken) {
          axios.put('/api/v1/family-users/token', { fcmRegistrationToken: token.value }).then(
            () => {
              localStorage.setItem('fcmRegistrationToken', token.value);
            }
          );
        }
      });

      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          if (notificationCheckTimeout) clearTimeout(notificationCheckTimeout);
          setIsCheckingNotification(false);

          const data = notification?.notification?.data ?? {};

          let parsedData: any = {};
          try {
            const raw = data?.data;
            const level1 = typeof raw === 'string' ? JSON.parse(raw) : raw;
            parsedData = level1?.data ?? level1 ?? data;
          } catch {
            parsedData = data;
          }

          const eventType: string = parsedData?.eventType ?? parsedData?.EventType ?? '';
          const id: string = parsedData?.targetEntityId ?? parsedData?.Id ?? '';
          const roomId: string = parsedData?.roomId ?? '';

          if (roomId) {
            history({
              search: qs.stringify({
                ...queryParams(),
                chatOpened   : true,
                selectedChat : roomId,
              }),
            });
          } else if (eventType) {
            const taskEvents    = ['task-added', 'task-updated', 'task-deleted', 'task-reminder', 'TaskAdded', 'TaskUpdated', 'TaskDeleted', 'TaskReminder'];
            const docEvents     = ['document-added', 'document-updated', 'document-deleted', 'DocumentAdded', 'DocumentUpdated', 'DocumentDeleted'];
            const contactEvents = ['contact-added', 'contact-updated', 'contact-deleted', 'ContactAdded', 'ContactUpdated', 'ContactDeleted'];
            const eventEvents   = ['event-added', 'event-updated', 'event-deleted', 'event-reminder', 'event-reminder-all', 'EventAdded', 'EventUpdated', 'EventDeleted', 'EventReminder'];
            const checkinEvents = ['checkin', 'check-in', 'Checkin'];

            if (taskEvents.includes(eventType)) {
              history(`${EAppRoutes.TasksSummary}/${id}`);
            } else if (docEvents.includes(eventType)) {
              history(`${EAppRoutes.DocumentsSummary}/${id}`);
            } else if (contactEvents.includes(eventType)) {
              history(`${EAppRoutes.ContactsSummary}/${id}`);
            } else if (eventEvents.includes(eventType)) {
              history(`${EAppRoutes.Schedule}#key=${id}`);
            } else if (checkinEvents.includes(eventType)) {
              history(EAppRoutes.Locate);
            } else {
              history(EAppRoutes.Dashboard);
            }
          } else {
            history(EAppRoutes.Dashboard);
          }
        }
      );

      return () => {
        if (notificationCheckTimeout) clearTimeout(notificationCheckTimeout);
      };
    },
    [isNative]
  );

  useEffect(() => {
    const storedCookieConsent = localStorage.getItem('cookieConsent');
    setCookieConsent(storedCookieConsent);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const storedCookieConsent = localStorage.getItem('cookieConsent');
      setCookieConsent(storedCookieConsent);
    }, 3000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!selected && !isLoading) {
      updateSelectedFamily(families?.[0]?.id || '');
    }
  }, [ isLoading ]);

  const handleSubmit = () => {
    families?.map((family) => {
      if (family?.id === selected?.id && !family?.isActivePayment && family?.isCreator) {
        setIsUser(false);
      } else if (family?.id === selected?.id && !family?.isActivePayment && !family?.isCreator) {
        setIsUser(true);
      } else if(family?.id === selected?.id && family?.isActivePayment){
        setIsUser(false);
      }
      return setPage(EAppRoutes.Dashboard);
    });
  };

  if (isCheckingNotification) return null;

  return (
    <SelectPlanWrapper>
      <HeaderContainer maxWidth="md">
        <PageHeader
          color="#fff"
          mb="0"
          mbMd="0"
        >
          <StyledLogo />

          <WrapperGrid container>
            <Grid
              item
              sm={6}
              md={5}
            >
              <SelectPlanContainer>
                {(families?.length !== 0) ? (
                  <>
                    <TittleText>Select family</TittleText>

                    <StyledDropdown
                      fullWidth
                      variant="outlined"
                    >
                      <Select
                        displayEmpty
                        value={selectedFamilyId}
                        onChange={(event: any) => {
                          setSelectedFamilyId(event.target.value);
                          updateSelectedFamily(event.target.value);
                          handleSubmit();
                        }}
                      >
                        <MenuItem value="">
                        Choose a family
                        </MenuItem>

                        {(families || []).map((item) => (
                          <MenuItem
                            key={item.id}
                            value={item.id}
                          >
                            <FamilyName>{capitalizeFirstLetter(item.name)} Family</FamilyName>
                          </MenuItem>
                        ))}
                      </Select>
                    </StyledDropdown>

                    <MuiButton
                      fullWidth
                      disabled={!selectedFamilyId || isLoadingSelect || cookieConsent === null}
                      type="button"
                      margin="0 0 20px 0"
                      onClick={() => {
                        history(page);
                      }}
                    >
                      Submit
                    </MuiButton></>
                ) : (
                  <>
                    <TittleTextExpired>
                      Your account was previously cancelled and deleted. You can create a new family by restoring your previous email address or use a different email address.
                    </TittleTextExpired>

                    <MuiButton
                      fullWidth
                      type="button"
                      margin="0"
                      onClick={() => {
                        history(EAuthRoutes.Checkout);
                      }}
                    >
                      Same email
                    </MuiButton>

                    <MuiButton
                      fullWidth
                      type="button"
                      margin="10px 0"
                      onClick={() => {
                        history(EAuthRoutes.SignUp);
                      }}
                    >
                      Another email
                    </MuiButton>
                  </>
                )}

                <CreateFamilyWrapperGrid
                  item
                  xs={12}
                >
                  <MuiButton
                    type="button"
                    margin="10px 0"
                    onClick={logout}
                  >
                        Log Out
                  </MuiButton>
                </CreateFamilyWrapperGrid>
              </SelectPlanContainer>
            </Grid>

            <Grid
              item
              sm={6}
              md={7}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <AuthSVGSceneHolder>
                <img
                  src={imageCheckout}
                  alt="Checkout"
                />
              </AuthSVGSceneHolder>
            </Grid>
          </WrapperGrid>
        </PageHeader>
      </HeaderContainer>
    </SelectPlanWrapper>
  );
};

